/** OAuth redirect fragment interception with deep-link preservation. Per D-25, DATA-LAYER.md Section 6.3. */

import { supabase, isDevBypass } from "./auth";

const RETURN_ROUTE_KEY = "terum_return_route";

/**
 * Remember where the user was headed before the OAuth round trip.
 *
 * The route lives in the URL HASH (this is a HashRouter app) and every OAuth redirect drops the
 * hash, so a deep link — a briefing's `#/conversations/<id>` Slack link, a shared note — landed on
 * the default surface after sign-in. The implicit branch below reads an OAuth `state` param, but
 * no call site ever sent one, so that path had never once run. sessionStorage works for BOTH the
 * implicit and PKCE branches and doesn't require fighting the Supabase client over `state`.
 *
 * Mirrors the briefing-token stash in lib/briefing-intent.ts, which exists for the same reason.
 */
export function stashReturnRoute(hash: string): void {
  // Only in-app hash routes. Anything else (a protocol, a scheme-relative //host) is refused so a
  // crafted link cannot turn sign-in into an open redirect.
  const route = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!route.startsWith("/") || route.startsWith("//")) return;
  if (route === "/" || route === "/briefing") return; // nothing worth restoring
  try {
    sessionStorage.setItem(RETURN_ROUTE_KEY, route);
  } catch {
    // Private-mode/storage-disabled: deep-link restoration is a nicety, never a blocker.
  }
}

/** Read-and-clear the stashed route. Returns null when there is nothing to restore. */
export function popReturnRoute(): string | null {
  try {
    const route = sessionStorage.getItem(RETURN_ROUTE_KEY);
    sessionStorage.removeItem(RETURN_ROUTE_KEY);
    return route && route.startsWith("/") && !route.startsWith("//") ? route : null;
  } catch {
    return null;
  }
}

/**
 * Handles OAuth redirect fragments and legacy URL normalization.
 * MUST run before HashRouter initializes (Pitfall 3).
 * Preserves the intended post-login route via the OAuth state param.
 */
export async function handleAuthRedirect(): Promise<boolean> {
  console.log("[auth-redirect] URL:", window.location.href);
  console.log("[auth-redirect] hash:", window.location.hash);
  console.log("[auth-redirect] search:", window.location.search);

  // D-25: Skip redirect handling in dev bypass mode
  if (isDevBypass()) return false;

  const hash = window.location.hash;

  // T-11-02-01: OAuth callback -- consume access_token fragment before React renders
  if (hash && hash.includes("access_token=")) {
    console.log("[auth-redirect] found access_token in hash, consuming...");
    const params = new URLSearchParams(hash.slice(1));
    // `state` first for any provider that round-trips it; otherwise the route we stashed before
    // the redirect. Default is the live home surface — it used to be "/home", itself a retired
    // address that only redirects.
    const intendedRoute = params.get("state") || popReturnRoute() || "/briefing";

    const { data } = await supabase.auth.getSession();
    console.log("[auth-redirect] after getSession:", data.session ? "session OK" : "NO session");
    window.location.hash = intendedRoute.startsWith("/") ? intendedRoute : `/${intendedRoute}`;
    return true;
  }

  // PKCE flow: code comes as query param, not hash
  const code = new URLSearchParams(window.location.search).get("code");
  if (code) {
    console.log("[auth-redirect] found PKCE code in query, exchanging...");
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);
    console.log("[auth-redirect] code exchange:", data.session ? "session OK" : "FAILED", error?.message);
    window.history.replaceState({}, "", window.location.pathname);
    // This branch strips the URL outright, so without the stash a PKCE sign-in ALWAYS lost the
    // deep link — worse than the implicit branch, which at least had a (never-populated) state param.
    const intendedRoute = popReturnRoute();
    if (data.session && intendedRoute) window.location.hash = intendedRoute;
    return !!data.session;
  }

  // Pitfall 5: Legacy URL normalization -- #home -> #/home
  if (hash && !hash.startsWith("#/") && !hash.includes("=")) {
    window.location.hash = `/${hash.slice(1)}`;
    return false;
  }

  return false;
}
